import React, { useState } from 'react';
import { T } from '../tokens';
import { useTheme } from '../theme';
import { Ico } from '../icons';

export interface FieldLabelProps {
  children?: React.ReactNode;
  required?: boolean;
  hint?: React.ReactNode;
}

export function FieldLabel({ children, required, hint }: FieldLabelProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 7 }}>
      <label style={{ fontSize: 12.5, fontWeight: 700, color: T.fg }}>
        {children}
        {required && <span style={{ color: T.error, marginLeft: 3 }}>*</span>}
      </label>
      {hint && <span style={{ fontSize: 11, color: T.fg3, fontWeight: 600 }}>{hint}</span>}
    </div>
  );
}

export interface FieldProps {
  label?: React.ReactNode;
  required?: boolean;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export function Field({ label, required, hint, error, children, style = {} }: FieldProps) {
  return (
    <div style={{ ...style }}>
      {label && <FieldLabel required={required} hint={hint}>{label}</FieldLabel>}
      {children}
      {error && (
        <div style={{ fontSize: 11.5, color: T.error, fontWeight: 600, marginTop: 6, display: 'flex', gap: 5, alignItems: 'center' }}>
          <Ico.alert size={13} />{error}
        </div>
      )}
    </div>
  );
}

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'style'> {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  icon?: React.ReactNode;
  error?: boolean | string;
  type?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  style?: React.CSSProperties;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

export function Input({ value, onChange, placeholder, icon, error, type = 'text', onFocus, onBlur, style = {}, inputMode, ...rest }: InputProps) {
  const t = useTheme();
  const [f, setF] = useState(false);
  const bc = error ? T.error : f ? t.accent : T.border;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 9, height: 48, padding: '0 14px',
      background: T.surface, border: `1.5px solid ${bc}`, borderRadius: t.radius,
      boxShadow: f && !error ? `0 0 0 3px ${t.accent}1a` : 'none',
      transition: 'all 150ms', ...style,
    }}>
      {icon && <span style={{ color: f ? t.accent : T.fg3, display: 'flex', flexShrink: 0 }}>{icon}</span>}
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        inputMode={inputMode}
        {...rest}
        onFocus={(e) => { setF(true); onFocus && onFocus(e); }}
        onBlur={(e) => { setF(false); onBlur && onBlur(e); }}
        style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', fontSize: 15, fontFamily: T.font, fontWeight: 500, color: T.fg }}
      />
    </div>
  );
}

export interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'> {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
}

export function Textarea({ value, onChange, placeholder, rows = 3, style = {}, ...rest }: TextareaProps) {
  const t = useTheme();
  const [f, setF] = useState(false);
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      onFocus={() => setF(true)}
      onBlur={() => setF(false)}
      {...rest}
      style={{
        width: '100%', padding: '12px 14px',
        background: T.surface, border: `1.5px solid ${f ? t.accent : T.border}`,
        borderRadius: t.radius, boxShadow: f ? `0 0 0 3px ${t.accent}1a` : 'none',
        fontSize: 15, fontFamily: T.font, fontWeight: 500, color: T.fg,
        outline: 0, resize: 'vertical', boxSizing: 'border-box',
        transition: 'all 150ms', ...style,
      }}
    />
  );
}
